import { useRequest } from '@hourly/hooks/useRequest';
import { useState } from 'react';
import useEventBus from '@shared/data/hooks/useEventBus/useEventBus';
import { type SettingEvents } from '../events/externalEventMapping';
import { Document, DocumentType, UploadUrl } from '../model/document.model';
import {
  deleteDocumentRequest,
  downloadDocumentRequest,
  fetchUploadUrlForDocumentRequest,
  getDocumentsRequest,
  uploadFileToObjectStorageRequest,
} from '../request/documents.request';

function useDownloadDocument() {
  const { request: downloadDocument } = useRequest(
    async ({ partnerId, documentId }: { partnerId: string; documentId: string }) => {
      return await downloadDocumentRequest(partnerId, documentId);
    },
    { requestOnMount: false }
  );
  return downloadDocument;
}

export function useDocuments(partnerId: string) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  useEventBus<SettingEvents>('settings:documents:document_uploaded', () => {
    setIsUploading(false);
    fetchDocuments();
  });

  const { request: fetchDocuments, status } = useRequest(
    async () => {
      if (!partnerId) {
        return;
      }
      setDocuments(await getDocumentsRequest(partnerId));
    },
    { requestOnMount: true }
  );

  const { request: deleteDocument, status: deleteStatus } = useRequest(
    async ({ partnerId, documentId }: { partnerId: string; documentId: string }) => {
      await deleteDocumentRequest(partnerId, documentId);
      fetchDocuments();
    },
    { requestOnMount: false }
  );

  const { request: uploadDocument, status: uploadStatus } = useRequest(
    async ({ partnerId, file }: { partnerId: string; file: File }) => {
      const uploadUrl: UploadUrl = await fetchUploadUrlForDocumentRequest(partnerId, file);
      await uploadFileToObjectStorageRequest(uploadUrl, file);
      setIsUploading(true);
    },
    { requestOnMount: false }
  );

  return {
    documents: documents.filter(
      (doc) => doc.type == DocumentType.w9 || doc.type == DocumentType.partnerReferralAgreement
    ),
    otherDocuments: documents.filter((doc) => doc.type === DocumentType.other),
    downloadDocument: useDownloadDocument(),
    uploadDocument: uploadDocument,
    deleteDocument: deleteDocument,
    isSettled:
      (status.isError || status.isSuccess) &&
      (deleteStatus.isError || deleteStatus.isSuccess || deleteStatus.isPristine) &&
      (uploadStatus.isError || uploadStatus.isSuccess || uploadStatus.isPristine) &&
      !isUploading,
  };
}
